// Chemin: C:\gestion-copro\src\pages\admin-saas\NettoyageGEDPage.tsx
// Session 255 : Nettoyage GED — suppression des documents zombies
// Un zombie = document présent en Firestore mais invisible dans la GED
// Cas 1 : document sans fichierUrl ET sans contenuTexte (archivage incomplet)
// Cas 2 : courrier miroir orphelin (documentGedId absent ou document GED supprimé)

import React, { useState, useCallback } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  deleteDoc,
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { deleteDocument } from '../../services/document.service';
import {
  Trash2,
  Search,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  FileX,
  Info,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

type TypeZombie =
  | 'archivage_incomplet' // pas de fichierUrl ni contenuTexte
  | 'courrier_orphelin';  // courrier miroir dont le document GED n'existe plus

interface DocumentZombie {
  id: string;
  coproprieteId: string;
  titre: string;
  type: string;
  source?: string;
  fichierUrl?: string;
  createdAt?: any;
  typeZombie: TypeZombie;
  courrierMiroirId?: string; // ID du courrier miroir associé si existe
}

interface CourrierOrphelin {
  id: string;
  coproprieteId: string;
  objet: string;
  numero?: string;
  documentGedId?: string;
  createdAt?: any;
}

// ============================================
// UTILITAIRES
// ============================================

const formatDate = (ts: any): string => {
  if (!ts) return '—';
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return '—';
  }
};

const labelTypeZombie: Record<TypeZombie, { label: string; color: string; desc: string }> = {
  archivage_incomplet: {
    label: 'Archivage incomplet',
    color: 'bg-red-100 text-red-700',
    desc: 'Document sans fichier ni contenu — archivage interrompu',
  },
  courrier_orphelin: {
    label: 'Courrier orphelin',
    color: 'bg-yellow-100 text-yellow-700',
    desc: 'Courrier miroir dont le document GED associé a été supprimé',
  },
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

export default function NettoyageGEDPage() {
  const [scanning, setScanning] = useState(false);
  const [zombies, setZombies] = useState<DocumentZombie[]>([]);
  const [courriersOrphelins, setCourriersOrphelins] = useState<CourrierOrphelin[]>([]);
  const [scanFait, setScanFait] = useState(false);
  const [suppressionEnCours, setSuppressionEnCours] = useState<Set<string>>(new Set());
  const [supprimes, setSupprimes] = useState<Set<string>>(new Set());
  const [erreurs, setErreurs] = useState<Record<string, string>>({});

  // ============================================
  // SCAN
  // ============================================

  const lancerScan = useCallback(async () => {
    setScanning(true);
    setScanFait(false);
    setZombies([]);
    setCourriersOrphelins([]);
    setSupprimes(new Set());
    setErreurs({});

    try {
      const zombiesDetectes: DocumentZombie[] = [];

      // Un vrai zombie = document sans fichierUrl ET sans contenuTexte
      // Les miroirs email/courrier avec contenuTexte sont des documents VALIDES
      const allDocs = await getDocs(collection(db, 'documents'));
      for (const d of allDocs.docs) {
        const data = d.data();
        if (!data.fichierUrl && !data.contenuTexte) {
          zombiesDetectes.push({
            id: d.id,
            coproprieteId: data.coproprieteId || '—',
            titre: data.titre || data.fichierNom || 'Sans titre',
            type: data.type || '—',
            source: data.source,
            createdAt: data.createdAt,
            typeZombie: 'archivage_incomplet',
          });
        }
      }

      setZombies(zombiesDetectes);

      // --- Cas 3 : Courriers miroirs orphelins ---
      const orphelins: CourrierOrphelin[] = [];
      const courriersSnap = await getDocs(
        query(collection(db, 'courriers'), where('sourceCreation', '==', 'sentinelle_vigie'))
      );
      for (const c of courriersSnap.docs) {
        const data = c.data();
        const documentGedId = data.documentGedId as string | undefined;
        if (!documentGedId) {
          // Courrier miroir sans documentGedId
          orphelins.push({
            id: c.id,
            coproprieteId: data.coproprieteId || '—',
            objet: data.objet || 'Sans objet',
            numero: data.numero,
            documentGedId: undefined,
            createdAt: data.createdAt,
          });
          continue;
        }
        // Vérifier si le document GED existe encore
        const gedSnap = await getDoc(doc(db, 'documents', documentGedId));
        if (!gedSnap.exists()) {
          orphelins.push({
            id: c.id,
            coproprieteId: data.coproprieteId || '—',
            objet: data.objet || 'Sans objet',
            numero: data.numero,
            documentGedId,
            createdAt: data.createdAt,
          });
        }
      }

      setCourriersOrphelins(orphelins);
    } catch (err) {
      console.error('[NettoyageGED] Erreur scan:', err);
    } finally {
      setScanning(false);
      setScanFait(true);
    }
  }, []);

  // ============================================
  // SUPPRESSION DOCUMENT ZOMBIE
  // ============================================

  const supprimerZombie = async (zombie: DocumentZombie) => {
    setSuppressionEnCours(prev => new Set(prev).add(zombie.id));
    try {
      await deleteDocument(zombie.id);
      setSupprimes(prev => new Set(prev).add(zombie.id));
    } catch (err: any) {
      setErreurs(prev => ({ ...prev, [zombie.id]: err.message || 'Erreur inconnue' }));
    } finally {
      setSuppressionEnCours(prev => { const n = new Set(prev); n.delete(zombie.id); return n; });
    }
  };

  // ============================================
  // SUPPRESSION COURRIER ORPHELIN
  // ============================================

  const supprimerCourrierOrphelin = async (courrier: CourrierOrphelin) => {
    const key = `courrier_${courrier.id}`;
    setSuppressionEnCours(prev => new Set(prev).add(key));
    try {
      await deleteDoc(doc(db, 'courriers', courrier.id));
      setSupprimes(prev => new Set(prev).add(key));
    } catch (err: any) {
      setErreurs(prev => ({ ...prev, [key]: err.message || 'Erreur inconnue' }));
    } finally {
      setSuppressionEnCours(prev => { const n = new Set(prev); n.delete(key); return n; });
    }
  };

  // ============================================
  // SUPPRESSION TOUT
  // ============================================

  const toutSupprimer = async () => {
    const zombiesRestants = zombies.filter(z => !supprimes.has(z.id));
    const orphelinsRestants = courriersOrphelins.filter(c => !supprimes.has(`courrier_${c.id}`));
    for (const z of zombiesRestants) await supprimerZombie(z);
    for (const c of orphelinsRestants) await supprimerCourrierOrphelin(c);
  };

  // ============================================
  // STATS
  // ============================================

  const zombiesRestants = zombies.filter(z => !supprimes.has(z.id));
  const orphelinsRestants = courriersOrphelins.filter(c => !supprimes.has(`courrier_${c.id}`));
  const totalRestants = zombiesRestants.length + orphelinsRestants.length;

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="space-y-6">

      {/* En-tête */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Nettoyage GED</h1>
          <p className="text-gray-600 mt-1">
            Détecte et supprime les documents zombies — présents en Firestore mais invisibles dans la GED.
          </p>
        </div>
        <button
          onClick={lancerScan}
          disabled={scanning}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          {scanning
            ? <Loader2 className="w-4 h-4 animate-spin" />
            : <Search className="w-4 h-4" />
          }
          {scanning ? 'Scan en cours...' : 'Lancer le scan'}
        </button>
      </div>

      {/* Légende types de zombies */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <div className="flex items-start gap-2 mb-3">
          <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <span className="text-sm font-medium text-blue-800">Types de zombies détectés</span>
        </div>
        <div className="space-y-1.5">
          {Object.entries(labelTypeZombie).map(([key, val]) => (
            <div key={key} className="flex items-start gap-2 text-sm">
              <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${val.color}`}>{val.label}</span>
              <span className="text-blue-700">{val.desc}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Résultats scan */}
      {scanFait && (
        <>
          {/* Résumé */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">{totalRestants}</div>
                  <div className="text-xs text-gray-500">zombies restants</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-orange-600">{zombiesRestants.length}</div>
                  <div className="text-xs text-gray-500">documents GED</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">{orphelinsRestants.length}</div>
                  <div className="text-xs text-gray-500">courriers orphelins</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">{supprimes.size}</div>
                  <div className="text-xs text-gray-500">supprimés</div>
                </div>
              </div>
              {totalRestants > 0 && (
                <button
                  onClick={toutSupprimer}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
                >
                  <Trash2 className="w-4 h-4" />
                  Tout supprimer ({totalRestants})
                </button>
              )}
              {totalRestants === 0 && scanFait && (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle2 className="w-5 h-5" />
                  <span className="text-sm font-medium">GED propre</span>
                </div>
              )}
            </div>
          </div>

          {/* Documents GED zombies */}
          {zombies.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                <FileX className="w-4 h-4 text-orange-500" />
                <h2 className="font-semibold text-gray-900">Documents GED zombies ({zombies.length})</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {zombies.map(zombie => {
                  const estSupprime = supprimes.has(zombie.id);
                  const enCours = suppressionEnCours.has(zombie.id);
                  const erreur = erreurs[zombie.id];
                  const config = labelTypeZombie[zombie.typeZombie];
                  return (
                    <div
                      key={zombie.id}
                      className={`flex items-start justify-between gap-4 px-4 py-3 transition-colors ${estSupprime ? 'bg-green-50 opacity-60' : 'hover:bg-gray-50'}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${config.color}`}>
                            {config.label}
                          </span>
                          <span className="text-xs font-mono text-gray-400">{zombie.type}</span>
                          <span className="text-xs text-gray-400">{zombie.coproprieteId}</span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 truncate">{zombie.titre}</p>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                          <span>ID: {zombie.id}</span>
                          <span>{formatDate(zombie.createdAt)}</span>
                          {zombie.courrierMiroirId && (
                            <span className="text-blue-500">Courrier miroir: {zombie.courrierMiroirId}</span>
                          )}
                        </div>
                        {erreur && (
                          <p className="text-xs text-red-600 mt-1">{erreur}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {estSupprime ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : enCours ? (
                          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                        ) : (
                          <button
                            onClick={() => supprimerZombie(zombie)}
                            className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                            title="Supprimer ce zombie"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Courriers orphelins */}
          {courriersOrphelins.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                <h2 className="font-semibold text-gray-900">Courriers miroirs orphelins ({courriersOrphelins.length})</h2>
              </div>
              <div className="divide-y divide-gray-100">
                {courriersOrphelins.map(courrier => {
                  const key = `courrier_${courrier.id}`;
                  const estSupprime = supprimes.has(key);
                  const enCours = suppressionEnCours.has(key);
                  const erreur = erreurs[key];
                  return (
                    <div
                      key={courrier.id}
                      className={`flex items-start justify-between gap-4 px-4 py-3 transition-colors ${estSupprime ? 'bg-green-50 opacity-60' : 'hover:bg-gray-50'}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${labelTypeZombie.courrier_orphelin.color}`}>
                            Courrier orphelin
                          </span>
                          {courrier.numero && (
                            <span className="text-xs font-mono text-gray-400">{courrier.numero}</span>
                          )}
                          <span className="text-xs text-gray-400">{courrier.coproprieteId}</span>
                        </div>
                        <p className="text-sm font-medium text-gray-900 truncate">{courrier.objet}</p>
                        <div className="flex items-center gap-3 mt-0.5 text-xs text-gray-500">
                          <span>ID: {courrier.id}</span>
                          <span>{formatDate(courrier.createdAt)}</span>
                          {courrier.documentGedId
                            ? <span className="text-red-500">Doc GED supprimé: {courrier.documentGedId}</span>
                            : <span className="text-orange-500">Pas de documentGedId</span>
                          }
                        </div>
                        {erreur && (
                          <p className="text-xs text-red-600 mt-1">{erreur}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {estSupprime ? (
                          <CheckCircle2 className="w-5 h-5 text-green-500" />
                        ) : enCours ? (
                          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                        ) : (
                          <button
                            onClick={() => supprimerCourrierOrphelin(courrier)}
                            className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                            title="Supprimer ce courrier orphelin"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* GED propre */}
          {zombies.length === 0 && courriersOrphelins.length === 0 && (
            <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
              <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-gray-900 mb-1">GED propre</h3>
              <p className="text-gray-500 text-sm">Aucun document zombie détecté sur l'ensemble des copropriétés.</p>
            </div>
          )}

          {/* Bouton rescan */}
          <div className="flex justify-center">
            <button
              onClick={lancerScan}
              className="inline-flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm"
            >
              <RefreshCw className="w-4 h-4" />
              Relancer le scan
            </button>
          </div>
        </>
      )}

      {/* État initial */}
      {!scanFait && !scanning && (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Search className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">Prêt à scanner</h3>
          <p className="text-gray-500 text-sm">Lance le scan pour détecter les documents zombies sur toutes les copropriétés.</p>
        </div>
      )}

    </div>
  );
}
